import { useEffect } from "react";
import { observer } from "mobx-react-lite";
import { useTranslation } from "react-i18next";
import { X } from "lucide-react";
import { useStore } from "../../stores/rootStore";
import {
	resolvePurchaseConfirm,
	usePurchaseConfirm,
} from "../../lib/purchaseBridgeActions";

/**
 * Host-side purchase confirmation, rendered OUTSIDE the sandbox so a playable
 * can never spend the user's Mubux without an explicit tap. Driven by the
 * `requestPurchaseConfirm` store (see PlayableSandbox's `economy.purchase`
 * intercept). Mounted once at the app layout, alongside FriendBridgeModal.
 */
export const PurchaseConfirmModal = observer(() => {
	const request = usePurchaseConfirm();
	const { wallet } = useStore();
	const { t } = useTranslation();

	// Show the live Mubux balance so the user knows what they're spending against.
	useEffect(() => {
		if (request) void wallet.refreshBalances();
	}, [request, wallet]);

	if (!request) return null;

	const cancel = () => resolvePurchaseConfirm(false);
	const confirm = () => resolvePurchaseConfirm(true);
	const offerName = request.title || request.offerId;
	const insufficient =
		request.priceMubux !== null && wallet.mubux < request.priceMubux;

	return (
		<div className="fixed inset-0 z-50 grid place-items-center bg-black/70 px-4 backdrop-blur-sm">
			<div className="w-full max-w-md rounded-3xl border border-white/10 bg-[#171021] p-5 shadow-2xl">
				<div className="mb-4 flex items-center justify-between">
					<h2 className="text-xl font-black">{t("play.purchaseTitle")}</h2>
					<button
						type="button"
						onClick={cancel}
						className="grid size-9 place-items-center rounded-full text-muted hover:bg-white/10 hover:text-white"
					>
						<X size={18} />
					</button>
				</div>

				<div className="rounded-2xl bg-white/5 p-4">
					<div className="flex items-center justify-between gap-3">
						<div className="min-w-0">
							<div className="truncate text-base font-black">{offerName}</div>
							{request.playableTitle && (
								<div className="truncate text-xs text-muted">{request.playableTitle}</div>
							)}
						</div>
						{request.priceMubux !== null && (
							<div className="shrink-0 text-lg font-black text-brand">
								{t("play.purchasePrice", { amount: request.priceMubux })}
							</div>
						)}
					</div>
				</div>

				<p className="mt-4 text-sm text-muted">
					{t("play.purchaseBalance", { amount: wallet.mubux })}
				</p>
				{insufficient && (
					<p className="mt-2 text-sm text-danger">{t("play.purchaseInsufficient")}</p>
				)}

				<div className="mt-5 grid grid-cols-2 gap-3">
					<button
						type="button"
						onClick={cancel}
						className="rounded-full border border-white/10 px-4 py-2 text-sm font-bold text-muted hover:text-white"
					>
						{t("common.cancel")}
					</button>
					<button
						type="button"
						onClick={confirm}
						disabled={insufficient}
						className="btn-primary justify-center py-2 text-sm disabled:opacity-60"
					>
						{t("play.purchaseConfirm")}
					</button>
				</div>
			</div>
		</div>
	);
});
